<template>
	<div id="whatIsME" class="what">
    <h1 class="display-2 py-4 text-xs-left" style="font-weight: 500;">Understanding ME/CFS</h1>
    <h4 class="headline" style="color: #248;">Myalgic Encephalomyelitis / Chronic Fatigue Syndrome</h4>
    <br/>
    <div class="mx-4 text-xs-left title">
      <p>ME/CFS is a serious long-term illness.
        It affects people of all ages, ethnicities and both male and female.
        Overseas studies suggest the prevalence of ME/CFS is 4 in 1000 people,
        implying that there are 20,000 people affected in NZ.
        Therefore, it is considerably more common than illnesses such as Multiple Sclerosis.
      </p>
    </div>
    <br/>
    <br/>
    <div>
      <div id="causes" class="headline" style="color: #248;">Possible Causes</div>
      <div class="mx-4 text-xs-left title">
        <br/>
        <p style="line-height: 125%">The causes and the specific pathologies of ME/CFS aren&rsquo;t yet understood. &nbsp;It often starts after an infectious illness such as glandular fever. &nbsp;Researchers are finding issues with energy production, blood circulation and oxygen use.</p>
        <br/>
      </div>
      <div id="symptoms" class="headline" style="color: #248;">Symptoms</div>
      <div class="mx-4 text-xs-left title">
        <br/>
        <h5 class="title">Core Symptoms</h5>
        <br/>
        <ul style="line-height: 125%">
          <li style="margin-bottom: 5px">
            <span style="font-weight: bold;">Greatly lowered ability to do activities that were usual before the illness.</span>
            <br/>This drop in activity level occurs along with fatigue and must last six months or longer (3 months in children).
          </li>
          <li style="margin-bottom: 5px">
            <span style="font-weight: bold;">Worsening of symptoms after physical or mental activity that would not have caused a problem before the illness.</span>
            <br/>This is known as Post-Exertional Malaise (PEM). &nbsp;PEM is often delayed, typically happening 12 to 24 hours after exertion. &nbsp;People may be house-bound or even completely bed-bound during PEM.
          </li>
          <li style="margin-bottom: 5px">
            <span style="font-weight: bold;">Sleep problems.</span><br/>People with ME may not feel better even after a full night&rsquo;s sleep. &nbsp;At times, people with ME may have trouble falling asleep or staying asleep, may sleep for over 12 hours a day or may have a delayed sleep onset.
          </li>
          <li style="margin-bottom: 5px">
            <span style="font-weight: bold;">Problems with cognition.</span>
            <br/>Most people with ME/CFS have trouble thinking quickly and concentrating for periods of time compared to prior to illness onset; there may be trouble finding words or slurring of speech, particularly during PEM.
          </li>
          <li style="margin-bottom: 5px">
            <span style="font-weight: bold;">Worsening of symptoms while standing or sitting upright.</span>
            <br/>This includes feeling lightheaded, dizzy, weak, or faint while standing or sitting up. They may have vision changes like blurring or seeing spots. This is known as Orthostatic Intolerance.
          </li>
        </ul>
        <br/>
        <h5 class="title">Additional Symptoms</h5>
        <br/>
        <div class="indent">
          <p style="font-weight: bold;">Pain is very common in people with ME/CFS and may include:</p>
          <ul style="line-height: 125%">
            <li><span>Muscle pain and aches and rapid muscle tiring</span></li>
            <li><span>Joint pain without swelling or redness </span></li>
            <li><span>Headaches, either new or worsening</span></li>
            <li><span>Nerve pain, felt as stabbing, burning, tingling, pins and needles</span></li>
            <li><span>A sore throat that happens often</span></li>
          </ul>
          <br/>
          <p style="font-weight: bold;">Some people with ME/CFS may also have:</p>
          <ul style="line-height: 125%">
            <li><span>Fibromyalgia </span></li>
            <li><span>Tender lymph nodes in the neck or armpits</span></li>
            <li><span>Digestive issues like nausea, heartburn, diarrhoea, constipation, abdominal pain</span></li>
            <li><span>Chills and night sweats</span></li>
            <li><span>Allergies and sensitivities to foods and medications</span></li>
            <li><span>Sensitive to chemicals, odours, light and noise</span></li>
          </ul>
        </div>
        <br/>
      </div>
      <div id="diagnosis" class="headline" style="color: #248;">Diagnosis</div>
      <div class="mx-4 text-xs-left title">
        <br/>
        <h5 class="title">Diagnostic Criteria</h5>
        <br/>
        <div class="indent">
          <p style="font-weight: bold;">The following 3 symptoms are required for diagnosis:</p>
          <ul style="line-height: 125%">
            <li><span>Greatly lowered ability to do activities that were usual before the illness that has lasted for 6 months or longer</span></li>
            <li><span>PEM, worsening of symptoms after physical or mental activity that would not have caused a problem before the illness</span></li>
            <li><span>Sleep problems</span></li>
          </ul>
          <br/>
          <p style="font-weight: bold;">In addition, at least one of the following:</p>
          <ul style="line-height: 125%">
            <li><span>Problems with cognition</span></li>
            <li><span>Orthostatic intolerance</span></li>
          </ul>
        </div>
        <br/>
        <h5 class="title">Diagnostic process</h5>
        <br/>
        <div class="indent">
          <p style="font-weight: bold;">Your doctor may:</p>
          <ul style="line-height: 125%">
            <li><span>Ask about you and your family&rsquo;s health history</span></li>
            <li><span>Ask about anything you have tried that makes the symptoms better or worse</span></li>
            <li><span>Suggest keeping a diary of symptoms and activity</span></li>
            <li><span>Do a physical exam or take blood tests to rule out any illnesses that may cause similar symptoms. </span></li>
          </ul>
        </div>
        <br/>
        <h5 class="title">Currently there is no established diagnostic test for ME/CFS</h5>
        <br/>
      </div>
      <div id="treatment" class="headline" style="color: #248;">Treatment</div>
      <div class="mx-4 text-xs-left title">
        <br/>
        <p style="line-height: 125%">Currently there is no cure or proven treatments for ME/CFS. Graded Exercise Therapy is no longer recommended.
            Appropriate management of the symptoms can provide some relief and improve quality of life.
            Over time some people, especially children and adolescents, do experience partial or full recovery.</p>
        <p style="line-height: 125%">Some people choose to seek out alternative therapies however these are unproven and often costly.
            Please consult your GP before starting any alternative therapies or supplements as they may be harmful.</p>
      </div>
      <div id="management" class="headline" style="color: #248;">Management</div>
      <div class="mx-4 text-xs-left title">
        <br/>
        <h5 class="title">Learn about ME</h5>
          <br/>
          <ul style="line-height: 125%">
            <li><span>Come to our monthly support group</span></li>
            <li><span>Join an online group - see our links</span></li>
            <li><span>Talk to our Community Facilitator for assistance</span></li>
          </ul>
          <br/>
        <h5 class="title">Symptom Management</h5>
          <br/>
          <p style="line-height: 125%">The main management approach is reducing activity to a level that can be sustained and that avoids Post-Exertional Malaise (the crash that results from too much exertion).
            This may mean replacing school with distance education, or reducing hours worked, as well as using energy saving approaches like online shopping.
          </p>
          <p style="line-height: 125%">Given that your energy is limited, plan so that you can spend it on the activities that are most important to you.
            Planning to rest before and after an activity can reduce the risk of PEM. Pacing yourself to avoid PEM is one of the most important ways to manage ME/CFS
          </p>
          <p style="line-height: 125%">Find a doctor you trust to work with you. He or she may be able to help with pain, sleep problems and orthostatic intolerance (problems with being upright),
              avoiding de-conditioning and managing food sensitivities as well as arrangements for home care, financial support and counselling.
          </p>
        <h5 class="title">Emotional support</h5>
          <br/>
          <p style="line-height: 125%">ME/CFS is not easy to cope with. Make sure that you have people to help you. Our monthly support group or online groups can put you in touch with people who understand.
              If you are concerned about your mental health consider seeking professional support for this. Cognitive Behavioural Therapy is not a cure for ME/CFS but can help establish good coping mechanisms.</p>
      </div>
    </div>
    <br/>
    <br/>
  </div>
</template>

<style>
.indent { margin-left: 10px; }
</style>

<script>
export default {
	name: "Understanding"
}
</script>